/**
 * Soul Service
 *
 * API calls for the user SOUL — persona, priorities, and usage tracking.
 *
 * @author FacilityAI Team
 */

import { VITE_CORE_API_URL } from "@/constants/env";
import { auth } from "@/services/auth-service";

// ─── Types (mirrors backend models) ───

export type PersonaType =
	| "facility_manager"
	| "executive"
	| "general_manager"
	| "location_worker"
	| "finance"
	| "analyst"
	| "other";

export type PriorityType =
	| "uptime"
	| "cost_control"
	| "compliance"
	| "safety"
	| "energy_efficiency"
	| "tenant_satisfaction";

export type ScopeType =
	| "single_location"
	| "few_locations"
	| "regional"
	| "national"
	| "corporate_all";

export type InfoStyleType =
	| "quick_summaries"
	| "detailed_reports"
	| "visual_charts"
	| "just_alerts";

export interface UserSoul {
	id: number;
	userId: number;
	organizationId: number;
	persona: PersonaType;
	personaOther?: string | null;
	priorities: PriorityType[];
	scope?: ScopeType | null;
	currentChallenge?: string | null;
	infoStyle?: InfoStyleType | null;
	usageStats: Record<string, any>;
	evolvedPriorities?: PriorityType[] | null;
	lastEvolvedAt?: string | null;
	onboardingCompleted: boolean;
	createdAt: string;
	updatedAt: string;
}

export interface CreateSoulPayload {
	persona: PersonaType;
	personaOther?: string;
	priorities: PriorityType[];
	scope?: ScopeType;
	currentChallenge?: string;
	infoStyle?: InfoStyleType;
}

// ─── API Functions ───

/**
 * Get the current user's SOUL
 */
export const getUserSoul = async (): Promise<UserSoul | null> => {
	try {
		const response = await fetch(`${VITE_CORE_API_URL}/core/user/soul`, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
		});

		if (!response.ok) return null;

		const result = await response.json();
		return result.data || null;
	} catch (error) {
		console.error("Failed to fetch user soul:", error);
		return null;
	}
};

/**
 * Create or update the user's SOUL (onboarding completion)
 */
export const createUserSoul = async (payload: CreateSoulPayload): Promise<UserSoul | null> => {
	try {
		const response = await fetch(`${VITE_CORE_API_URL}/core/user/soul`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
			body: JSON.stringify(payload),
		});

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}));
			throw new Error(errorData.message || "Failed to create soul");
		}

		const result = await response.json();
		return result.data;
	} catch (error) {
		console.error("Failed to create user soul:", error);
		throw error;
	}
};

/**
 * Update the user's SOUL
 */
export const updateUserSoul = async (payload: Partial<CreateSoulPayload>): Promise<UserSoul | null> => {
	try {
		const response = await fetch(`${VITE_CORE_API_URL}/core/user/soul`, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
			body: JSON.stringify(payload),
		});

		if (!response.ok) return null;

		const result = await response.json();
		return result.data;
	} catch (error) {
		console.error("Failed to update user soul:", error);
		return null;
	}
};

/**
 * Track a usage event (fire-and-forget)
 */
export const trackUsage = (event: "page_visit" | "query_topic" | "feature_usage", target: string): void => {
	// Fire-and-forget — no await, no error handling that blocks the user
	auth?.currentUser?.getIdToken().then((token) => {
		fetch(`${VITE_CORE_API_URL}/core/user/soul/track`, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${token}`,
			},
			body: JSON.stringify({ event, target }),
		}).catch(() => {});
	}).catch(() => {});
};
